import {get, reduce, filter, isUndefined, groupBy, forEach, isString, find, isNaN, uniqBy, map, sumBy, sum} from "lodash"

import {
  ENABLED,
  TYPE_DRIVER,
  DEFAULT_DRIVER_ENGAGEMENT_QUESTION_TEXT as DDEQT,
  TYPE_LIKERT,
  OUTRO_STARTED,
  INTRO_STARTED,
  OUTRO_COMPLETE,
  TRIGGER_OUTRO,
  DIRECTION_FORWARD,
  DIRECTIONS,
  DIRECTION_BACKWARDS
} from "@irati/insights/constants/question"
import {FINISHED} from "@irati/insights/constants/userSurveyStatus"

/**
 *
 * @type {string}
 * @private
 */
const _DIRECTION_NEXT = "next"
const _DIRECTION_BACK = "back"

/**
 *
 * @param question
 * @param questionNumber
 * @param totalQuestions
 * @returns {{question: *, questionNumber: *, totalQuestions: *}}
 * @private
 */
const _returnQuestionObject = (question, questionNumber, totalQuestions) => {
  return {
    question,
    questionNumber,
    totalQuestions,
    isFirst: questionNumber === 1,
    isLast: questionNumber === totalQuestions
  }
}

/**
 * Return whether to use the driver comment question or not
 *
 * @param userSurvey
 * @returns {*|boolean}
 */
export const useDriverCommentQuestion = (userSurvey) => {
  return get(userSurvey, "preferences.includeDriverComment", false) || false
}

export const hasAnsweredDriverCommentQuestion = (userSurvey) => {
  return (get(userSurvey, "submittedDriverComments", []) || []).length > 0
}

/**
 * Return all enabled questions including the driver comment question if applicable
 *
 * @param userSurvey
 * @returns {object[]}
 */
export const getEnabledQuestions = (userSurvey) => {
  const enabledQuestions = (get(userSurvey, "questions", []) || []).filter((q) => q.status === ENABLED)
  const hasDriverCommentQuestion = useDriverCommentQuestion(userSurvey)
  const driverCommentTitle = get(userSurvey, "preferences.driverCommentTitle", "") || DDEQT

  /** Create the driver question. Use `TYPE_DRIVER` as an id. There'll only ever be one so don't need UUID */
  if (hasDriverCommentQuestion) {
    enabledQuestions.push({
      id: TYPE_DRIVER,
      type: TYPE_DRIVER,
      text: driverCommentTitle
    })
  }

  return enabledQuestions
}

/**
 * Takes a user survey and returns the number of active questions.
 * If the preference `includeDriverComment` is set increment the question count by 1.
 *
 * @param userSurvey {object}
 * @returns {number}
 */
export const questionCount = (userSurvey) => {
  const hasDriverCommentQuestion = get(userSurvey, "preferences.includeDriverComment", false) || false
  const questions = get(userSurvey, "questions", []) || []
  return reduce(
    questions,
    (s, q) => {
      return q.status === ENABLED ? s + 1 : s
    },
    hasDriverCommentQuestion ? 1 : 0
  )
}

export const getAllAnsweredQuestionIds = (userSurvey) => {
  return (get(userSurvey, "submittedAnswers", []) || []).map((a) => a.questionId)
}

/**
 * Return the first question to ask, based off of previously answered questions
 *
 * @param userSurvey {object}
 * @returns {object|null}
 */
export const getFirstUnansweredQuestion = (userSurvey) => {
  if (get(userSurvey, "status") === FINISHED) return null

  const enabledQuestions = getEnabledQuestions(userSurvey)
  const answeredIds = getAllAnsweredQuestionIds(userSurvey)
  let firstUnanswered = null
  let firstUnansweredNumber = 0

  /** loop to find a question that hasn't been answered */
  enabledQuestions.some((q, index) => {
    if (q.type === TYPE_LIKERT && !answeredIds.includes(q.id)) {
      firstUnanswered = q
      firstUnansweredNumber = index + 1
      return true
    }
  })

  /**
   * This is a bit of an after thought but for the first unanswered method this assumes that the status isn't FINISHED
   * so if we were to return null here the next logic is to auto finish the survey. This is a confusing flow for the
   * user, because in their eyes they never clicked `submit` to finish the survey. In this case, we want to simply
   * return the last question regardless of it's answered state.
   */
  if (!firstUnanswered) {
    firstUnanswered = enabledQuestions.slice(-1)[0]
    firstUnansweredNumber = enabledQuestions.length
  }

  return _returnQuestionObject(firstUnanswered, firstUnansweredNumber, enabledQuestions.length)
}

/**
 * Taking the user survey object, current question object and the next / back direction return the next question to ask.
 * Returning null assumes that the survey has finished.
 *
 * @param userSurvey {object}
 * @param currentQuestion {object}
 * @param direction {string}
 * @returns {{}}
 */
const _getNewQuestion = (userSurvey, currentQuestion, direction) => {
  const questions = getEnabledQuestions(userSurvey)
  const currentQuestionId = get(currentQuestion, "id")
  let newQuestion = null
  let newQuestionNumber = 0

  /** Match current question and return the next + */
  questions.forEach((q, index) => {
    if (q.id === currentQuestionId) {
      if (direction === _DIRECTION_NEXT) {
        newQuestion = questions[index + 1] || null
        newQuestionNumber = newQuestion ? index + 2 : newQuestionNumber
      } else {
        newQuestion = questions[index - 1] || null
        newQuestionNumber = newQuestion ? index : newQuestionNumber
      }
    }
  })

  return _returnQuestionObject(newQuestion, newQuestionNumber, questions.length)
}

/**
 * Taking the user survey object and the current question object return the next question to ask.
 * Returning null assumes that the survey has finished.
 *
 * @param userSurvey {object}
 * @param currentQuestion {object}
 * @returns {{}}
 */
export const getNextQuestion = (userSurvey, currentQuestion) => {
  return _getNewQuestion(userSurvey, currentQuestion, _DIRECTION_NEXT)
}

/**
 * Taking the user survey object and the current question object return the previous question to ask.
 * Returning null assumes that the survey has finished.
 *
 * @param userSurvey {object}
 * @param currentQuestion {object}
 * @returns {{}}
 */
export const getPreviousQuestion = (userSurvey, currentQuestion) => {
  return _getNewQuestion(userSurvey, currentQuestion, _DIRECTION_BACK)
}

/**
 * Taking the user survey and the question index, return the question object
 *
 * @param userSurvey
 * @param questionIndex
 * @returns {{question: *, questionNumber: *, totalQuestions: *}}
 */
export const getQuestionByIndex = (userSurvey, questionIndex) => {
  const questions = getEnabledQuestions(userSurvey)
  /** force it to be null instead of undefined if not found */
  const newQuestion = questions[questionIndex] || null
  const newQuestionNumber = newQuestion ? questionIndex + 1 : 0

  return _returnQuestionObject(newQuestion, newQuestionNumber, questions.length)
}

/**
 * Accepts a question and answer. Checks existing given answer for duplicates, replaces with the most up to date and
 * returns the object ready to patch to the server
 *
 * @param question
 * @param answer
 * @returns {{}}
 */
export const getPatchObjectAfterAnsweringQuestion = (userSurvey, question, answer) => {
  /** switch question type to check different keys holding answers */
  const isLikert = get(question, "type") === TYPE_LIKERT
  const patchAnswerKey = isLikert ? "submittedAnswers" : "submittedDriverComments"
  const answerIdField = isLikert ? "questionId" : "driverId"
  const questionIdField = isLikert ? "id" : "driverId"
  const refIdObject = isLikert ? question : answer

  /** Does the answer key already contain an answer for the question? If so remove it */
  let newAnswers = filter(userSurvey[patchAnswerKey], (a) => {
    return a[answerIdField] !== refIdObject[questionIdField]
  })

  /** Allow only one driver comment TEMP */
  if (!isLikert) newAnswers = []

  /** Pump in new answer and return the patch obj */
  newAnswers.push({
    [answerIdField]: refIdObject[questionIdField],
    ...answer
  })

  /** Server formatting for `submittedDriverComments`. `text` must not be blank, if it is remove from the list */
  if (!isLikert) {
    newAnswers = filter(newAnswers, (a) => a.text)
  }

  return {
    [patchAnswerKey]: newAnswers
  }
}

/**
 * A touch confusing but it's role is to take the reducer state, next question and transition state overrides then
 * return the active questionObj based off of those. It decides what the next question obj is and whether it should
 * be set as the active question obj or not based on if it's the first time a question is set or if the app is in the
 * correct transition state to move to the next question.
 *
 *
 * @param state
 * @param nextQuestionObjOverride
 * @param transitionStateOverride
 * @returns {*|{}|FooterNav.defaultProps.questionObj|ProgressBar.defaultProps.questionObj}
 */
export const getQuestionObjFromReducerState = (state, nextQuestionObjOverride, transitionStateOverride) => {
  const transitionState = isUndefined(transitionStateOverride) ? state.transitionState : transitionStateOverride
  const nextQuestionObj = isUndefined(nextQuestionObjOverride) ? state.nextQuestionObj : nextQuestionObjOverride
  const isFirstTimeSettingQuestion = !!(!state.questionObj && nextQuestionObj)

  return isFirstTimeSettingQuestion || transitionState === OUTRO_COMPLETE ? nextQuestionObj : state.questionObj
}

/**
 * Taking a transition state work out if it means the UI is transitioning. TRIGGER_OUTRO is included because we want
 * the UI to respond immediately when a patch or navigation action is triggered.
 *
 * @param transitionState
 * @returns {boolean}
 */
export const isTransitioning = (transitionState) => {
  return [TRIGGER_OUTRO, INTRO_STARTED, OUTRO_STARTED, OUTRO_COMPLETE].includes(transitionState)
}

/**
 * Set the transition direction based on the direction parameter, if it isn't a valid direction default to the existing
 * transitionDirection from state, if that is undefined default to DIRECTION_FORWARD.
 *
 * @param state
 * @param directionOverride
 * @returns {*}
 */
export const setTransitionDirectionFromReducerState = (state, directionOverride) => {
  return DIRECTIONS.includes(directionOverride) ? directionOverride : state.transitionDirection || DIRECTION_FORWARD
}

/**
 *
 * @param questionObj
 * @param serviceMethod
 * @param serviceArgs
 */
export const getTransitionDirectionFromNavigation = (questionObj, serviceMethod, ...serviceArgs) => {
  const selectedIndex = serviceArgs[0]

  switch (serviceMethod) {
    case getPreviousQuestion:
      return DIRECTION_BACKWARDS
    case getQuestionByIndex:
      return selectedIndex + 1 < questionObj.questionNumber ? DIRECTION_BACKWARDS : DIRECTION_FORWARD
    default:
      return DIRECTION_FORWARD
  }
}

/**
 * Internal helper to get properties from the question and question override objects. The original question inside the
 * question overrides object is nested inside the `original` property.
 *
 * @param question
 * @param property
 * @returns {string}
 * @private
 */
const _getQuestionProperty = (question, property) => {
  return get(question, `original.${property}`, get(question, property))
}

/**
 * Generate a consistent object for returning
 *
 *
 * @param driver
 * @param questions
 * @returns {{driver: {id: *}, questions: *}}
 * @private
 */
const _formatDriverQuestionObj = (driver, questions) => {
  const driverObj = isString(driver) ? {id: driver, name: driver} : driver

  return {
    driver: driverObj,
    questions
  }
}

/**
 * Given the question, tenant and override text update the overrides array and return the PUT object to be sent to the
 * server.
 *
 * @todo bit clunky comparing the text to empty the override, implement this differently when we have time.
 * @todo need to manage this properly based on existing overrides, for now always replace with a single item.
 * @param tenantId
 * @param question
 * @param text
 */
export const generatePutOverrideObject = (tenantId, question, text) => {
  return {
    text,
    tenantUuid: tenantId
  }
}

/**
 * Only return enabled questions
 *
 * @param questions {array}
 * @returns {array}
 */
export const filterEnabledQuestions = (questions) => {
  return filter(questions, (question) => _getQuestionProperty(question, "status") === ENABLED)
}

/**
 * Filters out the unanswered questions and gets the average score
 */

export const getAverageScore = (questions, selector = "questionStats") => {
  const list = filter(questions, (question) => get(question, `${selector}.answersAverage`) !== 0)
  const total = sumBy(list, `${selector}.timesAnswered`)
  const result = sum(list.map((q) => get(q, `${selector}.answersAverage`) * (get(q, `${selector}.timesAnswered`) / total)))
  return isNaN(result) ? 0 : result
}

/**
 * Creates the drivers array from questions
 */
export const getDriverListFromQuestions = (questions) => {
  const allDrivers = map(questions, "driver")
  return uniqBy(allDrivers, "id")
}

/** */
export const getDriverFromQuestions = (questions, driverId) => {
  const allDrivers = getDriverListFromQuestions(questions)
  return find(allDrivers, {id: driverId})
}

/**
 * Helper function, takes an array of questions and groups them by driverId.
 * If drivers are supplied, splice in full driver information
 *
 * @param questions {array}
 * @param drivers {array}
 * @returns {*}
 */
export const groupQuestionsByDriver = (questions, drivers, filterNil = false) => {
  const allDrivers = drivers || getDriverListFromQuestions(questions)
  const driverGroups = groupBy(questions, (question) => _getQuestionProperty(question, "driverId"))
  const driverGroupObjs = []

  /** Loop the groups and inject full driver object if found, then return as a formatted driver question obj */
  forEach(driverGroups, (driverQuestions, driverId) => {
    const driver = find(allDrivers, {id: driverId})
    if (!filterNil || (filterNil && driver)) {
      driverGroupObjs.push(_formatDriverQuestionObj(driver || driverId, driverQuestions))
    }
  })

  return driverGroupObjs
}

/**
 * Helper function, takes an array of questions, filters object including driverId and
 * questionsByDriver array to return driver specific or general amount of responses
 *
 * @param questions {array}
 * @param questionsByDriver {array}
 * @param filters {object}
 * @returns {integer}
 */
export const getResponseCount = (questions, filters, questionsByDriver) => {
  if (filters.driverId) {
    const specificDriverQuestions = find(questionsByDriver, (driver) => driver.driver.id === filters.driverId)
    return sumBy(specificDriverQuestions.questions, (q) => q.questionStats.timesAnswered)
  }
  return sumBy(questions, (q) => q.questionStats.timesAnswered)
}
